/**
 * planet-economy.ts — pure helpers translating `state.structures` into the
 * per-planet and per-empire economy numbers that `advance.ts` needs.
 *
 * Everything here is side-effect-free. The helpers read state; `advance-
 * PopulationAndEconomy` in advance.ts calls them each tick and folds the
 * results into the existing population / per-pop-output pipeline.
 *
 * Splitting these helpers out of advance.ts keeps the per-tick pass readable
 * and gives the M10 Ship Production milestone a clean import path for
 * `canBuildClassAtPlanet` (the Shipyard gate) without pulling in the whole
 * economy loop.
 *
 * Stacking rules (v1):
 *   - Output-multiplier kinds (Trade Center, Processing Plant, Research
 *     Lab, Refinery, Cloning Center): take the MAX active level across
 *     instances on the planet. Multiple copies of the same kind don't stack
 *     output — they waste a slot. Encourages upgrading rather than duping.
 *   - Storage kinds (Bank, Warehouse, Fuel Dump): SUM `level × STORAGE_CAP_PER_LEVEL`
 *     across all instances on all empire planets. MFix5.3 made each
 *     storage kind unique-per-planet; total cap still flows from the
 *     per-empire level sum.
 *   - Embassy: per-level curve (`EMBASSY_DIPLOMACY_BY_LEVEL` — L1 = 0.1,
 *     L2 = 0.2). Unique-per-planet, race-capped (RACE-AA L2, BB/CC L1).
 *     Empire output is the sum across active Embassies, capped at
 *     DIPLOMATIC_CAP.
 *   - Shipyard: MAX Shipyard level on the target planet (unique per the
 *     balance file, but max is defensive against bad state).
 *
 * "Active" means `level >= 1` — structures under initial construction
 * don't contribute any effects.
 */

import {
  baseCapFor,
  economyMultiplierForLevel,
  embassyDiplomacyAtLevel,
} from './balance-structures';
import { capBonusForEmpire } from './balance-structure-effects';
import type {
  EmpireId,
  GameState,
  PlanetId,
  Structure,
  StructureKind,
} from '../state/types';

// ---------------------------------------------------------------------------
// Per-planet output multipliers
// ---------------------------------------------------------------------------

export interface PlanetEconomyMultipliers {
  /** Credits output multiplier from the best Trade Center on the planet. */
  credits: number;
  /** Minerals output multiplier from the best Processing Plant. */
  minerals: number;
  /** Research output multiplier from the best Research Lab. */
  research: number;
  /** Fuel output multiplier from the best Refinery. */
  fuel: number;
  /** Pop growth multiplier from the best Cloning Center. */
  popGrowth: number;
}

const IDENTITY_MULTIPLIERS: Readonly<PlanetEconomyMultipliers> = Object.freeze({
  credits: 1,
  minerals: 1,
  research: 1,
  fuel: 1,
  popGrowth: 1,
});

/**
 * Compute the per-kind max-level multiplier for a planet. Iterates the
 * planet's structure ids once; returns identity (1.0 everywhere) if no
 * active economy structures exist. Cheap enough to call per planet per tick.
 */
export function economyMultipliersForPlanet(
  state: GameState,
  planetId: PlanetId,
): PlanetEconomyMultipliers {
  const planet = state.planets.get(planetId);
  if (!planet || planet.structureIds.length === 0) return { ...IDENTITY_MULTIPLIERS };

  // Track max level seen per kind. Missing kinds stay at 0 → identity (1.0).
  const maxLevel = new Map<StructureKind, number>();
  for (const id of planet.structureIds) {
    const s = state.structures.get(id);
    if (!s || s.level < 1) continue;
    const prev = maxLevel.get(s.kind) ?? 0;
    if (s.level > prev) maxLevel.set(s.kind, s.level);
  }

  const sd = state.structuresData;
  return {
    credits: economyMultiplierForLevel(sd, 'trade-center', maxLevel.get('trade-center') ?? 0),
    minerals: economyMultiplierForLevel(
      sd,
      'processing-plant',
      maxLevel.get('processing-plant') ?? 0,
    ),
    research: economyMultiplierForLevel(sd, 'research-lab', maxLevel.get('research-lab') ?? 0),
    fuel: economyMultiplierForLevel(sd, 'refinery', maxLevel.get('refinery') ?? 0),
    popGrowth: economyMultiplierForLevel(
      sd,
      'cloning-center',
      maxLevel.get('cloning-center') ?? 0,
    ),
  };
}

// ---------------------------------------------------------------------------
// Empire-wide resource caps
// ---------------------------------------------------------------------------

export interface EmpireResourceCaps {
  /** Credits cap = BASE_CREDITS_CAP + Σ (Bank.level × STORAGE_CAP_PER_LEVEL). */
  credits: number;
  /** Minerals cap = BASE_MINERALS_CAP + Σ (Warehouse.level × STORAGE_CAP_PER_LEVEL). */
  minerals: number;
  /** Fuel cap = BASE_FUEL_CAP + Σ (FuelDump.level × STORAGE_CAP_PER_LEVEL). */
  fuel: number;
}

/**
 * Compute credits / minerals / fuel caps for an empire by summing storage
 * structure levels across all its planets. Research is uncapped (flows into
 * research project) and Diplomatic has its own hard cap (DIPLOMATIC_CAP in
 * balance-structures.ts); neither is returned from this helper.
 */
export function resourceCapsForEmpire(
  state: GameState,
  empireId: EmpireId,
): EmpireResourceCaps {
  const sd = state.structuresData;
  const baseCredits = baseCapFor(sd, 'credits');
  const baseMinerals = baseCapFor(sd, 'minerals');
  const baseFuel = baseCapFor(sd, 'fuel');
  const empire = state.empires.get(empireId);
  if (!empire) {
    return { credits: baseCredits, minerals: baseMinerals, fuel: baseFuel };
  }
  // v3M27 — capBonusForEmpire walks every structure on every owned
  // planet and sums the matching `capBonus` effect's per-level entry.
  // Replaces the old bank/warehouse/fuel-dump kind-switch + flat
  // `storageCapPerLevel` global.
  return {
    credits: baseCredits + capBonusForEmpire(state, empire, 'credits'),
    minerals: baseMinerals + capBonusForEmpire(state, empire, 'minerals'),
    fuel: baseFuel + capBonusForEmpire(state, empire, 'fuel'),
  };
}

// ---------------------------------------------------------------------------
// Embassy Diplomatic Influence
// ---------------------------------------------------------------------------

/**
 * Diplomatic Influence generated per tick by an empire. MFix5.3 —
 * Embassy is unique-per-planet, race-capped (RACE-AA L2, BB/CC L1), and
 * scales by level via `EMBASSY_DIPLOMACY_BY_LEVEL`: L1 = 0.1, L2 = 0.2.
 * Caller applies `DIPLOMATIC_CAP` on the cumulative total.
 */
export function diplomaticPerTickForEmpire(
  state: GameState,
  empireId: EmpireId,
): number {
  const empire = state.empires.get(empireId);
  if (!empire) return 0;
  const sd = state.structuresData;
  let total = 0;
  for (const s of iterateEmpireStructures(state, empire.planetIds)) {
    if (s.kind !== 'embassy') continue;
    if (s.level < 1) continue;
    total += embassyDiplomacyAtLevel(sd, s.level);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Shipyard class gate
// ---------------------------------------------------------------------------

/**
 * Highest ship Class Level buildable at this planet, per the best active
 * Shipyard. 0 means no active Shipyard — nothing can be built here. Matches
 * STRUCTURES §2.2 "Shipyard at level N can build any chassis of Class Level ≤ N".
 *
 * M10 Ship Production will call this when validating CommandBuildShip.
 */
export function maxShipClassAtPlanet(state: GameState, planetId: PlanetId): number {
  const planet = state.planets.get(planetId);
  if (!planet) return 0;
  let best = 0;
  for (const id of planet.structureIds) {
    const s = state.structures.get(id);
    if (!s || s.kind !== 'shipyard' || s.level < 1) continue;
    if (s.level > best) best = s.level;
  }
  return best;
}

/** Sugar on `maxShipClassAtPlanet`. Returns true iff a Class N can be built here. */
export function canBuildClassAtPlanet(
  state: GameState,
  planetId: PlanetId,
  classLevel: number,
): boolean {
  return maxShipClassAtPlanet(state, planetId) >= classLevel;
}

// ---------------------------------------------------------------------------
// Internal iteration helper
// ---------------------------------------------------------------------------

function* iterateEmpireStructures(
  state: GameState,
  planetIds: readonly PlanetId[],
): Generator<Structure> {
  for (const pid of planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    for (const sid of planet.structureIds) {
      const s = state.structures.get(sid);
      if (s) yield s;
    }
  }
}
